<template>
  <!-- Design-system look: the design system's breadcrumbs (.ds-crumbs, airlst2027 design-system
       src/styles/components/nav.css): 13px secondary text, muted "/" separators, links turn brand
       on hover, the current page in primary medium. Shown right of the page headline. -->
  <nav
    v-if="crumbs.length > 1"
    ref="nav"
    class="min-w-0 items-center"
    :class="tooNarrow ? 'hidden' : 'flex'"
    :aria-label="$t('common.breadcrumbs')"
    data-test="breadcrumbs"
  >
    <ol
      ref="list"
      class="m-0 flex min-w-0 flex-wrap items-center gap-1.5 p-0 text-(length:--ds-font-size-sm) text-(--ds-text-secondary)"
    >
      <li
        v-for="(crumb, index) in visibleCrumbs"
        :key="crumb.key"
        class="flex items-center gap-1.5"
        :class="measuring ? 'flex-none' : 'min-w-0 last:flex-none'"
        :aria-current="crumb.href === null ? 'page' : undefined"
      >
        <span v-if="index > 0" class="text-(--ds-text-muted)" aria-hidden="true"
          >/</span
        >
        <span
          v-if="crumb.folded"
          class="cursor-default"
          :title="crumb.label"
          :aria-label="crumb.label"
          data-test="breadcrumb-folded"
          >…</span
        >
        <Link
          v-else-if="crumb.href !== null"
          :href="crumb.href"
          class="truncate no-underline transition-colors duration-150 hover:text-(--ds-text-brand)"
          :data-test="`breadcrumb-${crumb.key}`"
        >
          {{ crumb.label }}
        </Link>
        <span
          v-else
          class="truncate font-medium text-(--ds-text-primary)"
          :data-test="`breadcrumb-${crumb.key}`"
          v-text="crumb.label"
        />
      </li>
    </ol>
  </nav>
</template>

<script setup>
import { useBreadcrumbs } from '@/composables/breadcrumbs'
import { Link } from '@inertiajs/vue3'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

const crumbs = useBreadcrumbs()

const nav = useTemplateRef('nav')
const list = useTemplateRef('list')

/**
 * How many leading crumbs are folded into one "…".
 *
 * Where the trail has to stay on one line (the section headline, SectionTabs) and is too long for
 * it, every crumb used to shrink at once, down to "Ai… / My… / E." - each one unreadable. Folding
 * from the start instead keeps the crumbs nearest the page whole; the folded ones are in the "…"'s
 * tooltip. A trail that may wrap never folds.
 */
const foldedCount = ref(0)
const measuring = ref(false)

/** Not even "… / page" fits: the trail is left out, the headline beside it names the page. */
const tooNarrow = ref(false)

const visibleCrumbs = computed(() => {
  if (foldedCount.value === 0) {
    return crumbs.value
  }

  const folded = crumbs.value.slice(0, foldedCount.value)

  return [
    {
      key: 'folded',
      folded: true,
      label: folded.map((crumb) => crumb.label).join(' / '),
    },
    ...crumbs.value.slice(foldedCount.value),
  ]
})

const overflows = () => list.value.scrollWidth > list.value.clientWidth + 1

const fit = async () => {
  foldedCount.value = 0
  tooNarrow.value = false
  await nextTick()

  if (!list.value || getComputedStyle(list.value).flexWrap !== 'nowrap') {
    return
  }

  // Measured with every crumb at its full width, so an overflow shows instead of being absorbed by
  // the crumbs shrinking. The current page always stays.
  measuring.value = true
  await nextTick()

  while (overflows() && foldedCount.value < crumbs.value.length - 1) {
    foldedCount.value++
    await nextTick()
  }

  tooNarrow.value = overflows()
  measuring.value = false
}

/**
 * Watches the headline row rather than the trail: the trail's own width follows what it shows, so
 * it would refit itself after every fold.
 */
let observer = null
let observedWidth = null

onMounted(() => {
  fit()

  const row =
    nav.value?.closest('[data-breadcrumbs-row]') ?? nav.value?.parentElement

  if (!row || typeof ResizeObserver === 'undefined') {
    return
  }

  observer = new ResizeObserver(([entry]) => {
    const width = Math.round(entry.contentRect.width)

    if (width !== observedWidth) {
      observedWidth = width
      fit()
    }
  })
  observer.observe(row)
})

onBeforeUnmount(() => observer?.disconnect())

watch(crumbs, fit)
</script>
